/**
 * UsageDetailPanel — the popover opened from the header UsageBadge. Itemises
 * each configured provider's usage:
 *
 *   • OpenRouter — window spend vs the key's dashboard cap, remaining balance,
 *     and all-time spend (or raw spend when the key is uncapped).
 *   • Anthropic  — Claude account utilisation across the 5h / 7d / overage
 *     rate-limit windows, with reset times and the unified limit status.
 *
 * Only the providers whose probe returned ``ok`` are rendered (the parent
 * passes ``null`` for an unconfigured/unavailable provider). Shares its
 * primitives + types with the badge via usageShared.tsx.
 */

import { AnthropicLogo, OpenRouterLogo } from "@/components/common/ProviderLogos";
import type { ClaudeUsageResponse, OpenRouterUsageResponse } from "./usageShared";
import { RESET_WINDOW_LABEL, UsageBar, fmtUsd, shortReset } from "./usageShared";

/** Anthropic's unified limit status → a coloured pill. */
const LIMIT_STATUS: Record<string, { label: string; dot: string; text: string }> = {
  allowed: { label: "Healthy", dot: "bg-elf", text: "text-elf" },
  allowed_warning: { label: "Warning", dot: "bg-gold", text: "text-gold" },
  blocked: { label: "Blocked", dot: "bg-hydra", text: "text-hydra" },
};

function OpenRouterSection({ data }: { data: OpenRouterUsageResponse }) {
  const capped = data.limit_usd != null && data.limit_usd > 0 && data.usage_pct != null;
  // The cap resets on a cadence, so the headline figure is spend in the active
  // window (today / this week / this month) — not all-time, which can exceed
  // e.g. a daily cap.
  const windowUsd = data.usage_window_usd ?? data.usage_usd;
  const windowLabel = data.limit_reset ? RESET_WINDOW_LABEL[data.limit_reset] : undefined;

  return (
    <section>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-space">
          <OpenRouterLogo className="w-3.5 h-3.5 text-space-50" />
          OpenRouter
        </span>
        {data.is_free_tier ? (
          <span className="text-[9px] font-medium text-space-40 px-1.5 py-0.5 rounded-full bg-space-20">
            free tier
          </span>
        ) : data.label ? (
          <span className="text-[10px] text-space-40 truncate max-w-[8rem]" title={data.label}>
            {data.label}
          </span>
        ) : null}
      </div>

      {capped ? (
        <>
          <UsageBar label={windowLabel ?? "spend"} pct={data.usage_pct as number} />
          <div className="flex items-center justify-between mt-1 text-[10px] text-space-40 tabular-nums">
            <span>
              <span className="font-semibold text-space">{fmtUsd(windowUsd)}</span> / {fmtUsd(data.limit_usd)}
            </span>
            <span>
              {fmtUsd(data.remaining_usd)} left · {fmtUsd(data.usage_usd)} all-time
            </span>
          </div>
        </>
      ) : (
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-space tabular-nums">
            {fmtUsd(data.usage_usd)}
            <span className="ml-1 text-[10px] font-normal text-space-40">spent all-time</span>
          </span>
          <span className="text-[10px] text-space-40">no limit set</span>
        </div>
      )}
    </section>
  );
}

function AnthropicSection({ data }: { data: ClaudeUsageResponse }) {
  const status = data.limit_status ? LIMIT_STATUS[data.limit_status] : undefined;
  return (
    <section>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-space">
          <AnthropicLogo className="w-3.5 h-3.5 text-space-50" />
          Anthropic
        </span>
        {status && (
          <span className={`flex items-center gap-1 text-[10px] font-medium ${status.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {data.five_hour && (
          <UsageBar label="Session 5h" pct={data.five_hour.utilization} reset={shortReset(data.five_hour.resets_at)} />
        )}
        {data.seven_day && (
          <UsageBar label="Weekly 7d" pct={data.seven_day.utilization} reset={shortReset(data.seven_day.resets_at)} />
        )}
        {data.overage && data.overage.utilization > 0 && (
          <UsageBar label="Overage" pct={data.overage.utilization} reset={shortReset(data.overage.resets_at)} />
        )}
      </div>
    </section>
  );
}

export function UsageDetailPanel({
  openrouter,
  claude,
  onClose,
  onRefresh,
  refreshing,
}: {
  openrouter: OpenRouterUsageResponse | null;
  claude: ClaudeUsageResponse | null;
  onClose: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div
      className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw_-_1rem)] rounded-lg border border-space-20 bg-surface shadow-lg p-3 z-50 animate-[scaleIn_120ms_ease-out]"
      role="dialog"
      aria-label="Provider usage details"
    >
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-xs font-semibold text-space">Usage</h3>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh usage"
            title="Re-check usage now"
            className="text-space-40 hover:text-space transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg
              className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close usage details"
            className="text-space-40 hover:text-space transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {openrouter && <OpenRouterSection data={openrouter} />}
      {openrouter && claude && <div className="my-2.5 border-t border-space-10" />}
      {claude && <AnthropicSection data={claude} />}
    </div>
  );
}
